import { useCurrentFrame, useVideoConfig } from 'remotion';
import { CompositeSegment } from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Segment/CompositeSegment';
import AudiogramComponent from '../AudiogramComponent/AudiogramComponent';
import VideoInCompositeSegment from './VideoInCompositeSegment';
import { useCompositeSegment } from './useCompositeSegment';
import { getAnimationStylesForFrame } from '@/App/remotion/MainPlayer/utils/animation/getAnimationStylesForFrame';

const PlayRangeComponent: React.FC<{
  segment: CompositeSegment;
  canEditSpeakerLabel: boolean;
  canManuallyReposition?: boolean;
  preventOverlappingWithOtherCrops?: boolean;
  offset?: number;
}> = ({
  segment,
  canEditSpeakerLabel,
  canManuallyReposition = false,
  preventOverlappingWithOtherCrops,
  offset = 0
}) => {
  const rawFrame = useCurrentFrame();
  const currentFrame = rawFrame + offset;
  const { fps } = useVideoConfig();
  const {
    commonStartFromForElements,
    layoutDetailsAtCurrentFrame,
    backgroundStyle,
    finalConfig,
    videosToShow,
    mostRecentLayoutChangeFrame,
    playableEffects
  } = useCompositeSegment({ segment, currentFrame });

  if (commonStartFromForElements === null) {
    return null;
  }

  if (layoutDetailsAtCurrentFrame?.layout === 'AUDIOGRAM') {
    return (
      <AudiogramComponent
        backgroundStyle={backgroundStyle}
        layoutDetailsAtCurrentFrame={layoutDetailsAtCurrentFrame}
        segment={segment}
        canEditSpeakerLabel={canEditSpeakerLabel}
        commonStartFromForElements={commonStartFromForElements}
        finalConfig={finalConfig}
        offset={offset}
      />
    );
  }

  const { style: animationStyle, effectBounds } = getAnimationStylesForFrame(currentFrame, playableEffects, fps);

  const shouldApplyAnimationToIndividualVideo =
    layoutDetailsAtCurrentFrame &&
    effectBounds &&
    effectBounds.fromFrame >= layoutDetailsAtCurrentFrame.fromFrame &&
    effectBounds.toFrame <= layoutDetailsAtCurrentFrame.toFrame &&
    videosToShow.length === 1;

  return (
    <div
      data-testid="composite-segment-preview"
      style={{
        position: 'relative',
        height: '100%',
        width: '100%',
        ...backgroundStyle,
        ...(shouldApplyAnimationToIndividualVideo ? {} : animationStyle)
      }}
    >
      {segment.videos.map(video => {
        const videoIndex = videosToShow.indexOf(video.id);
        const muted = (layoutDetailsAtCurrentFrame?.mutedVideosId ?? []).includes(video.id);
        const isInLayout = videosToShow.includes(video.id);

        // Hide speaker label for pip layout for bottom right video
        const isPIPLayoutEnabled = layoutDetailsAtCurrentFrame?.layout === 'PIP';
        const isPipBubble = videoIndex === 1 && isPIPLayoutEnabled;
        const speakerDetails = isPipBubble ? undefined : video.speakerDetails;

        return (
          <VideoInCompositeSegment
            key={video.id}
            video={{ ...video, speakerDetails }}
            videoIndex={videoIndex}
            finalConfig={finalConfig}
            compositeSegmentId={segment.id}
            hasScreenshareAtCurrentFrame={!!layoutDetailsAtCurrentFrame?.hasScreenshare}
            commonStartFromForElements={commonStartFromForElements}
            canEditSpeakerLabel={canEditSpeakerLabel}
            offset={offset}
            // This is done cause video not in scene are still rendered.
            canManuallyReposition={canManuallyReposition && videoIndex !== -1}
            cropInformation={layoutDetailsAtCurrentFrame?.videoCropPositionMap?.[video.id] || null}
            cropStyles={layoutDetailsAtCurrentFrame?.videoCropPositionStyles?.[video.id] || undefined}
            allCropInformation={layoutDetailsAtCurrentFrame?.videoCropPositionMap}
            muted={muted}
            layoutChangeFrame={mostRecentLayoutChangeFrame}
            preventOverlappingWithOtherCrops={preventOverlappingWithOtherCrops}
            animationStyles={shouldApplyAnimationToIndividualVideo && isInLayout ? animationStyle : {}}
            contentId={segment.contentId}
          />
        );
      })}
    </div>
  );
};

export default PlayRangeComponent;
